import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const FLOOR_PLANS = {
  groundFloor: "https://customer-assets.emergentagent.com/job_fa97b31e-b6a8-4dc5-ad90-f86d6a5dc05e/artifacts/0w00ozcl_Plan%20RdC.jpg",
  gardenLevel: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/0cre2db1_Plan%20RdJ.jpg",
};

const FloorPlanSection = () => {
  const { t } = useLanguage();
  const [selectedPlan, setSelectedPlan] = useState(null);

  const plans = [
    { 
      src: FLOOR_PLANS.groundFloor, 
      name: t('plans_ground'), 
      desc: t('plans_ground_desc')
    },
    { 
      src: FLOOR_PLANS.gardenLevel, 
      name: t('plans_garden'), 
      desc: t('plans_garden_desc')
    },
  ];

  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="floor-plan-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12 md:mb-16"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] font-['Playfair_Display'] mb-4"
            data-testid="floor-plan-title"
          >
            {t('plans_title')}
          </h2>
          <p className="text-[#5C5C59] text-base md:text-lg max-w-2xl">
            {t('plans_subtitle')}
          </p>
        </motion.div>

        {/* Floor Plans Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {plans.map((plan, index) => (
            <div 
              key={index} 
              className="group cursor-pointer"
              onClick={() => setSelectedPlan(plan)}
              data-testid={`floor-plan-${index}`}
            >
              {/* Plan Image */}
              <div className="relative overflow-hidden bg-[#F7F7F5] mb-4">
                <img
                  src={plan.src}
                  alt={`${plan.name} - Villa contemporaine 320m² Croissy-sur-Seine`}
                  className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-105"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-[#1A2F23]/0 group-hover:bg-[#1A2F23]/10 transition-all duration-300 flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm uppercase tracking-widest">
                    {t('plans_view') || 'Voir en grand'}
                  </span>
                </div>
              </div>
              
              {/* Plan Info */}
              <h3 className="text-xl font-medium text-[#1A1A1A] mb-2 font-['Playfair_Display']">
                {plan.name}
              </h3>
              <p className="text-sm text-[#5C5C59] leading-relaxed">
                {plan.desc}
              </p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Lightbox */}
      {selectedPlan && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedPlan(null)}
          data-testid="floor-plan-lightbox"
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
            onClick={() => setSelectedPlan(null)}
            data-testid="floor-plan-lightbox-close"
          >
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPlan.src}
              alt={selectedPlan.name}
              className="w-full h-auto object-contain bg-white"
            />
            <p className="text-white text-center mt-4 text-lg font-['Playfair_Display']">
              {selectedPlan.name}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default FloorPlanSection;
